import type { ReactNode } from 'react';
import {
  classeConhecida,
  estadoConhecido,
  O_QUE_O_ESTADO_PERMITE,
  ROTULO_DA_CLASSE,
  ROTULO_DO_ESTADO,
  TOM_DA_CLASSE,
  TOM_DO_ESTADO,
  type Tom,
} from './regras';

/**
 * Os selos do toolkit: o ESTADO de uma rotina num alvo e a CLASSE de efeito
 * de uma fase. Moram aqui, e não em cada tela, porque aparecem em quatro
 * lugares — a lista, a matriz, o histórico e o cartão de execução da conversa
 * — e o mesmo estado com duas cores em dois lugares ensina a não confiar na
 * cor.
 */

const COR_DO_TOM: Record<Tom, string> = {
  verde: 'bg-emerald-500/15 text-emerald-300 ring-emerald-700/50',
  azul: 'bg-sky-500/15 text-sky-300 ring-sky-700/50',
  ambar: 'bg-amber-500/15 text-amber-300 ring-amber-700/50',
  vermelho: 'bg-red-500/15 text-red-300 ring-red-700/60',
  cinza: 'bg-zinc-800 text-zinc-400 ring-zinc-700/60',
};

function Selo({ tom, titulo, children, teste }: { tom: Tom; titulo?: string; children: ReactNode; teste?: string }): React.JSX.Element {
  return (
    <span
      title={titulo}
      data-testid={teste}
      className={
        'inline-flex max-w-full min-w-0 items-center rounded px-1.5 py-0.5 text-[11px] ring-1 ring-inset ' +
        COR_DO_TOM[tom]
      }
    >
      {/* O texto corta no fim, nunca estica: um alvo de nome comprido não
          pode empurrar o selo para fora da borda num telefone. */}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
}

/**
 * O estado de uma rotina num alvo, com o que ele deixa rodar no `title`.
 * Estado que a tela não conhece sai CRU e cinza — nunca com a cor de outro.
 */
export function SeloDoEstado({ estado, prefixo }: { estado: string; prefixo?: string }): React.JSX.Element {
  const conhecido = estadoConhecido(estado);
  return (
    <Selo
      tom={conhecido ? TOM_DO_ESTADO[conhecido] : 'cinza'}
      titulo={conhecido ? O_QUE_O_ESTADO_PERMITE[conhecido] : undefined}
      teste="selo-estado"
    >
      {prefixo ? <span className="mr-1 font-mono opacity-70">{prefixo}</span> : null}
      {conhecido ? ROTULO_DO_ESTADO[conhecido] : estado || 'estado desconhecido'}
    </Selo>
  );
}

/**
 * A classe de efeito de uma fase (D30). Classe que a tela não conhece é
 * tratada como o pior caso — vermelha —, porque o cartão que a mostra é o que
 * autoriza rodar.
 */
export function SeloDaClasse({ classe }: { classe: string }): React.JSX.Element {
  const conhecida = classeConhecida(classe);
  return (
    <Selo tom={conhecida ? TOM_DA_CLASSE[conhecida] : 'vermelho'} teste="selo-classe">
      {conhecida ? ROTULO_DA_CLASSE[conhecida] : classe || 'classe desconhecida'}
    </Selo>
  );
}

/** Um selo neutro de texto livre (forma, projeto, arquivada). */
export function SeloNeutro({ children, tom = 'cinza' }: { children: ReactNode; tom?: Tom }): React.JSX.Element {
  return <Selo tom={tom}>{children}</Selo>;
}
